// frontend/src/i18n/index.ts

import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import ru from './locales/ru.json'
import en from './locales/en.json'
import kz from './kz.json'
import { homeAndServicesMessages } from './home-translations'
import { newsTranslations } from './news-translations'
import { vacancyTranslationMessages } from './vacancy-translation'

// ============================================
// Конфигурация i18n
// ============================================

const resources = {
  ru: {
    translation: { ...ru, ...homeAndServicesMessages.ru, ...newsTranslations['ru'], ...vacancyTranslationMessages['ru'] },
  },
  en: {
    translation: { ...en, ...homeAndServicesMessages.en, ...newsTranslations['en'], ...vacancyTranslationMessages['en'] },
  },
  kz: {
    translation: { ...kz, ...homeAndServicesMessages.ru, ...newsTranslations['kz'], ...vacancyTranslationMessages['kz'] },
  },
  uk: { translation: { ...en, ...homeAndServicesMessages.en, ...newsTranslations['uk'], ...vacancyTranslationMessages['uk'] } },
  be: { translation: { ...ru, ...homeAndServicesMessages.ru, ...newsTranslations['be'], ...vacancyTranslationMessages['be'] } },
  uz: { translation: { ...ru, ...homeAndServicesMessages.ru, ...newsTranslations['uz'], ...vacancyTranslationMessages['uz'] } },
  ky: { translation: { ...ru, ...homeAndServicesMessages.ru, ...newsTranslations['ky'], ...vacancyTranslationMessages['ky'] } },
  tg: { translation: { ...ru, ...homeAndServicesMessages.ru, ...newsTranslations['tg'], ...vacancyTranslationMessages['tg'] } },
  az: { translation: { ...ru, ...homeAndServicesMessages.ru, ...newsTranslations['az'], ...vacancyTranslationMessages['az'] } },
  hy: { translation: { ...ru, ...homeAndServicesMessages.ru, ...newsTranslations['hy'], ...vacancyTranslationMessages['hy'] } },
  ro: { translation: { ...en, ...homeAndServicesMessages.en, ...newsTranslations['ro'], ...vacancyTranslationMessages['ro'] } },
  de: { translation: { ...en, ...homeAndServicesMessages.en, ...newsTranslations['de'], ...vacancyTranslationMessages['de'] } },
  fr: { translation: { ...en, ...homeAndServicesMessages.en, ...newsTranslations['fr'], ...vacancyTranslationMessages['fr'] } },
  es: { translation: { ...en, ...homeAndServicesMessages.en, ...newsTranslations['es'], ...vacancyTranslationMessages['es'] } },
  zh: { translation: { ...en, ...homeAndServicesMessages.en, ...newsTranslations['zh'], ...vacancyTranslationMessages['zh'] } },
}

// Список поддерживаемых языков
export const SUPPORTED_LANGUAGES = {
  ru: { nativeName: 'Русский', flag: '🇷🇺' },
  en: { nativeName: 'English', flag: '🇬🇧' },
  kz: { nativeName: 'Қазақша', flag: '🇰🇿' },
  uk: { nativeName: 'Українська', flag: '🇺🇦' },
  be: { nativeName: 'Беларуская', flag: '🇧🇾' },
  uz: { nativeName: "Oʻzbekcha", flag: '🇺🇿' },
  ky: { nativeName: 'Кыргызча', flag: '🇰🇬' },
  tg: { nativeName: 'Тоҷикӣ', flag: '🇹🇯' },
  az: { nativeName: 'Azərbaycanca', flag: '🇦🇿' },
  hy: { nativeName: 'Հայերեն', flag: '🇦🇲' },
  ro: { nativeName: 'Română', flag: '🇲🇩' },
  de: { nativeName: 'Deutsch', flag: '🇩🇪' },
  fr: { nativeName: 'Français', flag: '🇫🇷' },
  es: { nativeName: 'Español', flag: '🇪🇸' },
  zh: { nativeName: '中文', flag: '🇨🇳' },
}

export const SUPPORTED_LANGUAGES_LIST = Object.keys(SUPPORTED_LANGUAGES) as Array<keyof typeof SUPPORTED_LANGUAGES>

export const DEFAULT_LANGUAGE = 'ru'

// ============================================
// Инициализация i18n
// ============================================

i18n
  // Интеграция с React
  .use(initReactI18next)
  // Инициализация
  .init({
    resources,
    lng: localStorage.getItem('language') || DEFAULT_LANGUAGE,
    supportedLngs: Object.keys(resources),
    fallbackLng: DEFAULT_LANGUAGE,
    debug: import.meta.env.DEV,
    
    interpolation: {
      escapeValue: false, // React уже экранирует
    },
    
    react: {
      useSuspense: true,
      transSupportBasicHtmlNodes: true,
      transKeepBasicHtmlNodesFor: ['br', 'strong', 'i', 'p', 'b', 'em'],
    },

    ns: ['translation'],
    defaultNS: 'translation',
  })

// ============================================
// Экспорт
// ============================================

export default i18n

// ============================================
// Хелперы для работы с переводами
// ============================================

/**
 * Получить текущий язык
 */
export const getCurrentLanguage = (): string => {
  return i18n.language || DEFAULT_LANGUAGE
}

/**
 * Получить список поддерживаемых языков
 */
export const getSupportedLanguages = () => {
  return SUPPORTED_LANGUAGES_LIST
}

/**
 * Проверить, поддерживается ли язык
 */
export const isLanguageSupported = (lang: string): boolean => {
  return (SUPPORTED_LANGUAGES_LIST as string[]).includes(lang)
}

/**
 * Получить название языка на его родном языке
 */
export const getLanguageNativeName = (lang: string): string => {
  return SUPPORTED_LANGUAGES[lang as keyof typeof SUPPORTED_LANGUAGES]?.nativeName || lang
}

/**
 * Получить флаг языка
 */
export const getLanguageFlag = (lang: string): string => {
  return SUPPORTED_LANGUAGES[lang as keyof typeof SUPPORTED_LANGUAGES]?.flag || '🌍'
}

/**
 * Сменить язык
 */
export const changeLanguage = async (lang: string): Promise<void> => {
  if (!isLanguageSupported(lang)) {
    console.warn(`Language ${lang} is not supported`)
    return
  }
  
  await i18n.changeLanguage(lang)
  localStorage.setItem('language', lang)
  document.documentElement.lang = lang
  document.documentElement.lang = lang
  document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr'
}

/**
 * Получить перевод с параметрами
 */
export const t = (key: string, options?: any): string => {
  const translation = i18n.t(key, options)
  return typeof translation === 'string' ? translation : String(translation)
}

/**
 * Форматирование даты с учетом языка
 */
export const formatDateLocale = (date: Date | string, _format: string = 'PP'): string => {
  const lang = getCurrentLanguage()
  const dateObj = typeof date === 'string' ? new Date(date) : date
  
  try {
    // Используем Intl.DateTimeFormat для форматирования
    const formatter = new Intl.DateTimeFormat(lang, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
    return formatter.format(dateObj)
  } catch {
    return dateObj.toLocaleDateString()
  }
}

/**
 * Форматирование числа с учетом языка
 */
export const formatNumberLocale = (num: number, options?: Intl.NumberFormatOptions): string => {
  const lang = getCurrentLanguage()
  try {
    return new Intl.NumberFormat(lang, options).format(num)
  } catch {
    return num.toString()
  }
}

/**
 * Форматирование валюты с учетом языка
 */
export const formatCurrencyLocale = (
  amount: number,
  currency: string = 'RUB',
  options?: Intl.NumberFormatOptions
): string => {
  const lang = getCurrentLanguage()
  try {
    return new Intl.NumberFormat(lang, {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      ...options,
    }).format(amount)
  } catch {
    return `${amount} ${currency}`
  }
}

/**
 * Форматирование относительного времени
 */
export const formatRelativeTimeLocale = (date: Date | string): string => {
  const lang = getCurrentLanguage()
  const dateObj = typeof date === 'string' ? new Date(date) : date
  const now = new Date()
  const diffMs = now.getTime() - dateObj.getTime()
  const diffSec = Math.floor(diffMs / 1000)
  const diffMin = Math.floor(diffSec / 60)
  const diffHour = Math.floor(diffMin / 60)
  const diffDay = Math.floor(diffHour / 24)
  const diffMonth = Math.floor(diffDay / 30)
  const diffYear = Math.floor(diffDay / 365)

  const rtf = new Intl.RelativeTimeFormat(lang, { numeric: 'auto' })

  if (diffYear > 0) return rtf.format(-diffYear, 'year')
  if (diffMonth > 0) return rtf.format(-diffMonth, 'month')
  if (diffDay > 0) return rtf.format(-diffDay, 'day')
  if (diffHour > 0) return rtf.format(-diffHour, 'hour')
  if (diffMin > 0) return rtf.format(-diffMin, 'minute')
  return rtf.format(-diffSec, 'second')
}

// ============================================
// Хук для использования переводов с типизацией
// ============================================

import { useTranslation as useI18nTranslation } from 'react-i18next'

/**
 * Типизированный хук useTranslation
 */
export const useTranslation = () => {
  const { t, i18n, ready } = useI18nTranslation()
  
  return {
    t: (key: string, options?: any) => t(key, options),
    i18n,
    ready,
    language: i18n.language,
    changeLanguage: (lang: string) => i18n.changeLanguage(lang),
    dir: i18n.language === 'ar' ? 'rtl' : 'ltr',
  }
}
